/**
 * On-Call Manager Dashboard
 * Main component with member management, configuration, and developer tools
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Stack,
  MessageBar,
  MessageBarType,
  Pivot,
  PivotItem
} from '@fluentui/react';
import { OnCallManagerProvider, useOnCallManager } from './contexts/OnCallManagerContext';
import { LoggingProvider, useLogging } from './contexts/LoggingContext';
import { useAuth } from '@auth';
import { hasPermission } from '@auth/permissions';
import CurrentMemberCard from './components/CurrentMemberCard';
import SetMemberCard from './components/SetMemberCard';
import MemberListCard from './components/MemberListCard';
import AlwaysSendCard from './components/AlwaysSendCard';
import NotificationSettingsCard from './components/NotificationSettingsCard';
import NotificationHistory from './components/NotificationHistory';
import SmsHistory from './components/SmsHistory';
import TicketEvaluator from './components/TicketEvaluator';
import LogViewer from './components/LogViewer';
import { SmsEnrollmentCard } from './components/SmsEnrollmentCard';
import SmsEnrollmentRegistry from './components/SmsEnrollmentRegistry';

interface Notification {
  type: MessageBarType;
  message: string;
}

const OnCallManagerContent: React.FC = () => {
  const { error } = useOnCallManager();
  const { user } = useAuth();
  const { info } = useLogging();
  const [notification, setNotification] = useState<Notification | null>(null);
  const [selectedPivot, setSelectedPivot] = useState<string>('dashboard');

  const isDeveloper = useMemo(() => hasPermission(user, 'platform.admin'), [user]);

  const canWrite = useMemo(
    () => hasPermission(user, 'oncall.write'),
    [user]
  );

  const canViewEnrollmentRegistry = useMemo(
    () => hasPermission(user, 'oncall.write'),
    [user]
  );

  const canViewManagerTabs = useMemo(() => canWrite, [canWrite]);

  const availablePivotKeys = useMemo(() => {
    const keys: string[] = [];

    if (canViewManagerTabs) {
      keys.push('dashboard', 'history', 'smsHistory');
    }

    if (canViewEnrollmentRegistry) {
      keys.push('enrollmentRegistry');
    }

    if (isDeveloper) {
      keys.push('evaluator', 'logs');
    }

    return keys;
  }, [canViewEnrollmentRegistry, canViewManagerTabs, isDeveloper]);

  const hasBottomTabs = availablePivotKeys.length > 0;

  useEffect(() => {
    if (availablePivotKeys.length === 0) {
      return;
    }

    if (!availablePivotKeys.includes(selectedPivot)) {
      setSelectedPivot(availablePivotKeys[0]);
    }
  }, [availablePivotKeys, selectedPivot]);

  useEffect(() => {
    info('OnCallManager: Component mounted', { isDeveloper, canWrite });
  }, [info, isDeveloper, canWrite]);

  // Clear notification after 15 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 15000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  // Display context error if present
  useEffect(() => {
    if (error) {
      setNotification({
        type: MessageBarType.error,
        message: error
      });
    }
  }, [error]);

  const showNotification = useCallback((type: MessageBarType, message: string) => {
    setNotification({ type, message });
  }, []);

  return (
    <Stack tokens={{ childrenGap: 20 }} styles={{ root: { padding: 20, maxWidth: 1400, margin: '0 auto' } }}>
      {notification && (
        <MessageBar
          messageBarType={notification.type}
          onDismiss={() => setNotification(null)}
          dismissButtonAriaLabel="Close"
        >
          {notification.message}
        </MessageBar>
      )}

      <SmsEnrollmentCard onNotification={showNotification} />

      {hasBottomTabs && (
        <Pivot
          selectedKey={selectedPivot}
          onLinkClick={(item) => item && setSelectedPivot(item.props.itemKey!)}
          headersOnly={true}
        >
          {canViewManagerTabs && <PivotItem headerText="Dashboard" itemKey="dashboard" />}
          {canViewManagerTabs && <PivotItem headerText="Notification History" itemKey="history" />}
          {canViewManagerTabs && <PivotItem headerText="SMS History" itemKey="smsHistory" />}
          {canViewEnrollmentRegistry && <PivotItem headerText="Enrollment Registry" itemKey="enrollmentRegistry" />}
          {isDeveloper && <PivotItem headerText="Ticket Evaluator" itemKey="evaluator" />}
          {isDeveloper && <PivotItem headerText="Logs" itemKey="logs" />}
        </Pivot>
      )}

      {selectedPivot === 'dashboard' && canViewManagerTabs && (
        <Stack tokens={{ childrenGap: 20 }}>
          {canWrite && (
            <>
              <Stack horizontal tokens={{ childrenGap: 20 }} wrap>
                <Stack.Item grow styles={{ root: { minWidth: 300, maxWidth: 500 } }}>
                  <CurrentMemberCard onNotification={showNotification} />
                </Stack.Item>
                
                <Stack.Item grow styles={{ root: { minWidth: 300, maxWidth: 500 } }}>
                  <SetMemberCard onNotification={showNotification} />
                </Stack.Item>
              </Stack>

              <Stack tokens={{ childrenGap: 20 }}>
                <Stack horizontal tokens={{ childrenGap: 20 }} wrap>
                  <Stack.Item grow styles={{ root: { minWidth: 300, maxWidth: 500 } }}>
                    <MemberListCard onNotification={showNotification} />
                  </Stack.Item>
                  
                  <Stack.Item grow styles={{ root: { minWidth: 300, maxWidth: 500 } }}>
                    <AlwaysSendCard onNotification={showNotification} />
                  </Stack.Item>
                </Stack>

                <NotificationSettingsCard onNotification={showNotification} />
              </Stack>
            </>
          )}

          {!canWrite && (
            <MessageBar messageBarType={MessageBarType.info}>
              You don't have permission to manage on-call settings. Use the SMS Enrollment section above to enroll your phone number for notifications.
            </MessageBar>
          )}
        </Stack>
      )}

      {selectedPivot === 'history' && canViewManagerTabs && (
        <NotificationHistory onNotification={showNotification} />
      )}

      {selectedPivot === 'smsHistory' && canViewManagerTabs && (
        <SmsHistory onNotification={showNotification} />
      )}

      {selectedPivot === 'enrollmentRegistry' && canViewEnrollmentRegistry && (
        <SmsEnrollmentRegistry onNotification={showNotification} />
      )}

      {selectedPivot === 'evaluator' && isDeveloper && (
        <TicketEvaluator onNotification={showNotification} />
      )}

      {selectedPivot === 'logs' && isDeveloper && (
        <LogViewer />
      )}
    </Stack>
  );
};

/**
 * OnCallManager - Main component with providers
 */
const OnCallManager: React.FC = () => {
  return (
    <LoggingProvider>
      <OnCallManagerProvider>
        <OnCallManagerContent />
      </OnCallManagerProvider>
    </LoggingProvider>
  );
};

export default OnCallManager;
